import { useState } from 'react';
import type { ReactElement } from 'react';
import { Link, Navigate, Route, Routes } from 'react-router-dom';
import { IDLE_LIMIT_MS } from './components/IdleSignOut';
import { Shell } from './components/Shell';
import { ErrorBanner, Loading } from './components/ui';
import { DEV_BYPASS, useAuth, SIGNED_OUT_KEY } from './lib/auth';
import { useResource } from './lib/hooks';
import type { Me } from './lib/types';
import { AccountabilityChartPage } from './pages/AccountabilityChartPage';
import { AccountManagementPage } from './pages/AccountManagementPage';
import { AdminPage } from './pages/AdminPage';
import { AuditTrailPage } from './pages/AuditTrailPage';
import { FeatureRequestsPage } from './pages/FeatureRequestsPage';
import { CharterPage } from './pages/CharterPage';
import { CompliancePage } from './pages/CompliancePage';
import { CsatFeedbackPage } from './pages/CsatFeedbackPage';
import { CsatPage } from './pages/CsatPage';
import { ClientOnboardingPage } from './pages/ClientOnboardingPage';
import { CsPage } from './pages/CsPage';
import { DashboardPage } from './pages/DashboardPage';
import { DependencyPage } from './pages/DependencyPage';
import { EmployeesPage } from './pages/EmployeesPage';
import { EosMeetingRunPage } from './pages/EosMeetingRunPage';
import { EosPage } from './pages/EosPage';
import { GwcPage } from './pages/GwcPage';
import { KpiWheelPage } from './pages/KpiWheelPage';
import { MaPage } from './pages/MaPage';
import { IntegrationPlanPage } from './pages/IntegrationPlanPage';
import { RecruitingPage } from './pages/RecruitingPage';
import { NocPage } from './pages/NocPage';
import { OmlPage } from './pages/OmlPage';
import { ChangeRequestPage } from './pages/ChangeRequestPage';
import { TicketAuditPage } from './pages/TicketAuditPage';
import { TrainingRequestPage } from './pages/TrainingRequestPage';
import { OperationsManualPage } from './pages/OperationsManualPage';
import { PeoplePage } from './pages/PeoplePage';
import { ProcessesPage } from './pages/ProcessesPage';
import { ProductCatalogPage } from './pages/ProductCatalogPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ReviewsPage } from './pages/ReviewsPage';
import { SalesPage } from './pages/SalesPage';
import { AutotaskMapPage } from './pages/AutotaskMapPage';
import { IntegrationsPage } from './pages/IntegrationsPage';
import { QuotesReportPage } from './pages/QuotesReportPage';
import { SdfReportPage } from './pages/SdfReportPage';
import { PrepostingReportPage } from './pages/PrepostingReportPage';
import { MisfitsReportPage } from './pages/MisfitsReportPage';
import { TechnicianCleanlinessReportPage } from './pages/TechnicianCleanlinessReportPage';
import { MspMetricsReportPage } from './pages/MspMetricsReportPage';
import { PnlReportPage } from './pages/PnlReportPage';
import { ProfitabilityReportPage } from './pages/ProfitabilityReportPage';
import { InvoiceTotalsReportPage } from './pages/InvoiceTotalsReportPage';
import { AssetWorkloadReportPage } from './pages/AssetWorkloadReportPage';
import { ClientReportPage } from './pages/ClientReportPage';
import { FirewallAssessmentPage } from './pages/FirewallAssessmentPage';
import { ContractSetupReportPage } from './pages/ContractSetupReportPage';
import { ManagedServicesReportPage } from './pages/ManagedServicesReportPage';
import { BillableLaborReportPage } from './pages/BillableLaborReportPage';
import { ScorecardItemsPage } from './pages/ScorecardItemsPage';
import { ServiceDeliveryPage } from './pages/ServiceDeliveryPage';
import { ServiceDisciplinesPage } from './pages/ServiceDisciplinesPage';
import { VtoPage } from './pages/VtoPage';

function SignIn() {
  const { signIn } = useAuth();
  /* Why the last session ended, once (lib/auth.ts signOutForInactivity). */
  const [reason] = useState(() => {
    try {
      const r = sessionStorage.getItem(SIGNED_OUT_KEY);
      sessionStorage.removeItem(SIGNED_OUT_KEY);
      return r;
    } catch {
      return null;
    }
  });
  return (
    <div className="loading" style={{ flexDirection: 'column', minHeight: '100vh' }}>
      <h1>MSP-OPS</h1>
      {reason === 'idle' && <p className="small strong">You were signed out after {IDLE_LIMIT_MS / 60_000} minutes without activity.</p>}
      {reason === 'manual' && <p className="small strong">You have been signed out.</p>}
      <p className="muted">Sign in with your work account to continue.</p>
      <button type="button" className="btn btn-primary mt-4" onClick={() => void signIn()}>
        Sign in with Microsoft
      </button>
    </div>
  );
}

/**
 * A page the signed-in person may not open.
 *
 * The nav already hides these, so this is for the URL that was bookmarked,
 * pasted or guessed. It says no rather than redirecting silently: bouncing
 * somebody to the dashboard with no explanation reads as the link being
 * broken, and they try again.
 */
function NoAccess({ what }: { what: string }) {
  return (
    <div className="card">
      <div className="card-body">
        <h2>No access</h2>
        <p className="muted mt-2">
          You do not have access to {what}. An administrator can grant it on your employee record.
        </p>
        <Link className="btn btn-ghost mt-4" to="/">
          Back to the dashboard
        </Link>
      </div>
    </div>
  );
}

export function App() {
  const { ready, account, signOut } = useAuth();
  const signedIn = DEV_BYPASS || account !== null;

  /**
   * The CSAT feedback page, before anything else.
   *
   * It is opened by a customer clicking a face in a ticket email — somebody
   * with no account in the tenant and no reason to have one. Every other
   * branch below this line ends in a sign-in prompt, so the check has to come
   * first, ahead even of `ready`: waiting on MSAL to initialise would put a
   * spinner in front of a page that never needs a token.
   *
   * Read from `location` rather than routed, for the same reason — the router
   * below only ever renders for a signed-in session.
   */
  if (window.location.pathname === '/feedback') {
    return <CsatFeedbackPage />;
  }

  /* Only fetch the profile once we believe there is a session; asking earlier
     just produces a guaranteed 401. */
  const { data: me, loading, error, errorStatus, reload } = useResource<Me>(signedIn ? '/me' : null);

  if (!ready) return <Loading label="Starting up" />;
  if (!signedIn) return <SignIn />;
  if (loading) return <Loading label="Signing in" />;

  if (error) {
    /* Only a 403 actually means "you are not allowed in". Treating every
       failure as an access problem sends people to ask an administrator for
       permission when the real fault is a server that is down — so the two
       cases are kept distinct here. */
    const isAccessDenied = errorStatus === 403;
    const isUnreachable = errorStatus === 0 || (errorStatus !== null && errorStatus >= 500);
    /* A rejected token cannot be retried, because the cached one is the
       problem — "Try again" just replays it. The only way out is to throw the
       session away, so on a 401 that is the button we lead with. Without it
       this screen is a dead end reachable only by clearing site data. */
    const isBadToken = errorStatus === 401;

    return (
      <div className="content">
        <h1>
          {isAccessDenied ? 'Access' : isBadToken ? 'Sign in again' : 'Cannot load the app'}
        </h1>
        <div className="mt-4">
          <ErrorBanner message={error} />
        </div>

        {isAccessDenied && (
          <p className="muted mt-4">
            Ask a platform administrator to grant you access to MSP-OPS.
          </p>
        )}

        {isBadToken && (
          <p className="muted mt-4">
            Your session is no longer valid for this app — usually because the token was issued
            before a change to the app registration. Signing out clears it and the next sign-in
            gets a fresh one.
          </p>
        )}

        {isUnreachable && (
          <div className="mt-4">
            <p className="muted">
              This is a server problem, not a permissions one — you do not need anyone to grant
              you access. The API is not responding.
            </p>
            <p className="muted mt-2">
              If you are running locally, check that the API is up on port 3001:
            </p>
            <pre className="mt-2">npm run dev</pre>
          </div>
        )}

        <div className="row mt-4">
          {isBadToken && !DEV_BYPASS && (
            <button type="button" className="btn btn-primary" onClick={signOut}>
              Sign out and start again
            </button>
          )}
          <button
            type="button"
            className={isBadToken ? 'btn btn-ghost' : 'btn btn-primary'}
            onClick={reload}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const isAdmin = me?.appRole === 'admin';
  /* The page list on the person's employee record (migration 144) — the same
     list the API checks behind every one of these pages. Admins have all. */
  const can = (page: string) => isAdmin || (me?.pages?.includes(page) ?? false);
  const guard = (page: string, what: string, element: ReactElement) =>
    can(page) ? element : <NoAccess what={what} />;

  return (
    <Routes>
      <Route element={<Shell me={me} />}>
        <Route index element={guard('dashboard', 'the Dashboard', <DashboardPage />)} />
        {/* Vision. Hiding the links is presentation; these guards and the
            requirePage on the matching routers are the actual rule. */}
        <Route path="/vto" element={guard('vto', 'the V/TO', <VtoPage />)} />
        <Route path="/oml" element={guard('oml', 'the OML Assessment', <OmlPage />)} />
        <Route path="/kpi-wheel" element={guard('kpi-wheel', 'the KPI Wheel', <KpiWheelPage />)} />
        <Route path="/reviews" element={guard('reviews', 'Business Review', <ReviewsPage />)} />
        <Route path="/charter" element={guard('charter', 'the COO Charter', <CharterPage />)} />
        <Route path="/dependency" element={guard('dependency', 'Ownership Dependency', <DependencyPage />)} />
        {/* EOS — the Elliott Operating System, moved in. Its rocks and issues
            ARE the V/TO's, so the page is worth granting with that in mind. */}
        <Route path="/eos" element={guard('eos', 'EOS', <EosPage me={me} />)} />
        {/* The meeting runner. Its own route rather than a tab of the page
            above: a running Level 10 is a mode, and the section the team is on
            has to survive a reload and a shared link. */}
        <Route path="/eos/meetings/:meetingId" element={guard('eos', 'EOS', <EosMeetingRunPage />)} />
        <Route path="/ma" element={isAdmin ? <MaPage /> : <NoAccess what="M&A" />} />
        <Route
          path="/integration-plan"
          element={guard('integration-plan', 'the Integration Plan', <IntegrationPlanPage isAdmin={isAdmin} />)}
        />
        <Route path="/chart" element={guard('chart', 'the Accountability Chart', <AccountabilityChartPage />)} />
        <Route path="/gwc" element={guard('gwc', 'the GWC Assessment', <GwcPage />)} />
        <Route path="/sales" element={guard('sales', 'Sales', <SalesPage />)} />
        <Route path="/compliance" element={guard('compliance', 'Compliance', <CompliancePage />)} />
        <Route path="/service-delivery" element={guard('service-delivery', 'Service Delivery', <ServiceDeliveryPage />)} />
        {/* The service manager's cadence, opened from the card on the page
            above — the same page, so the same grant. */}
        <Route
          path="/service-delivery/disciplines"
          element={guard('service-delivery', 'Service Delivery', <ServiceDisciplinesPage />)}
        />
        <Route path="/csat" element={guard('csat', 'CSAT', <CsatPage />)} />
        <Route path="/cs" element={guard('cs', 'CS', <CsPage />)} />
        <Route path="/projects" element={guard('projects', 'Projects', <ProjectsPage />)} />
        <Route path="/noc" element={guard('noc', 'the NOC', <NocPage />)} />
        <Route
          path="/client-onboarding"
          element={guard('client-onboarding', 'Client Onboarding', <ClientOnboardingPage />)}
        />
        {/* Two paths, one page. The slug makes a section ADDRESSABLE, which is
            what lets an SOP in Account Management link to the procedure itself
            rather than to a copy of it — see POST /am/sops/link. Without the
            optional form, "/operations-manual" would stop matching and the
            nav link would 404. */}
        <Route
          path="/operations-manual"
          element={guard('operations-manual', 'the Operations Manual', <OperationsManualPage />)}
        />
        <Route
          path="/operations-manual/:slug"
          element={guard('operations-manual', 'the Operations Manual', <OperationsManualPage />)}
        />
        <Route path="/change-requests" element={guard('change-requests', 'Change Requests', <ChangeRequestPage />)} />
        <Route path="/ticket-audit" element={guard('ticket-audit', 'Ticket Audit', <TicketAuditPage />)} />
        <Route path="/training-request" element={guard('training-request', 'Training Request', <TrainingRequestPage />)} />
        <Route path="/quotes" element={guard('quotes', 'Quotes', <QuotesReportPage />)} />
        <Route path="/sdf" element={guard('sdf', 'SDF', <SdfReportPage />)} />
        <Route path="/preposting" element={guard('preposting', 'Pre-posting', <PrepostingReportPage />)} />
        <Route path="/misfits" element={guard('misfits', 'Misfits', <MisfitsReportPage />)} />
        <Route path="/profitability" element={guard('profitability', 'Client Profitability', <ProfitabilityReportPage />)} />
        <Route path="/invoices" element={guard('invoices', 'Invoice Totals', <InvoiceTotalsReportPage />)} />
        <Route path="/contract-setup" element={guard('contract-setup', 'Contract Setup', <ContractSetupReportPage />)} />
        <Route path="/billable-labor" element={guard('billable-labor', 'Billable Labor', <BillableLaborReportPage />)} />
        <Route
          path="/technician-cleanliness"
          element={guard('technician-cleanliness', 'Technician Cleanliness', <TechnicianCleanlinessReportPage />)}
        />
        <Route path="/firewalls" element={guard('firewalls', 'the Firewall Assessment', <FirewallAssessmentPage />)} />
        <Route path="/configuration-items" element={guard('configuration-items', 'Asset Workload', <AssetWorkloadReportPage />)} />
        <Route path="/client-report" element={guard('client-report', 'Client Report', <ClientReportPage />)} />
        <Route path="/msp-metrics" element={guard('msp-metrics', 'MSP Top 20', <MspMetricsReportPage />)} />
        <Route path="/pnl" element={guard('pnl', 'PNL', <PnlReportPage />)} />
        <Route
          path="/managed-services"
          element={guard('managed-services', 'the Managed Services Forecast', <ManagedServicesReportPage />)}
        />
        <Route
          path="/account-management"
          element={guard('account-management', 'Account Management', <AccountManagementPage />)}
        />
        <Route path="/products" element={guard('products', 'the Product Catalogue', <ProductCatalogPage />)} />
        <Route path="/people" element={guard('people', 'People', <PeoplePage isAdmin={isAdmin} />)} />
        <Route path="/recruiting" element={guard('recruiting', 'Recruiting', <RecruitingPage />)} />
        {/* The operations admin dashboard, moved in (migration 049). */}
        <Route path="/admin" element={guard('admin', 'Admin', <AdminPage />)} />
        {/* Settings — what the app is configured with rather than what it
            records. Admin-only, both here and on the matching routers, except
            the directory: Employees can be granted to a manager, who gets
            themselves and the people under them and a read-only page. The
            scoping is GET /employees?scope=directory and the editing is
            requireAdmin; this guard only decides who may open it. */}
        <Route
          path="/employees"
          element={guard(
            'employees',
            'Employees',
            /* A review of somebody is a People endpoint, so offering it here
               to a manager who was never given People would be a button that
               403s. The page hides it instead. */
            <EmployeesPage isAdmin={isAdmin} canReview={isAdmin || can('people')} />,
          )}
        />
        {/* Under Settings, which is admin-only as a section — the same rule
            requireAdmin enforces on /api/audit. */}
        <Route
          path="/audit-trail"
          element={isAdmin ? <AuditTrailPage /> : <NoAccess what="Audit trail" />}
        />
        {/* Under Settings, admin-only as that whole section is. Anyone signed
            in may RAISE one from the top bar; this is the reading of them,
            and the same rule is requireAdmin on GET /api/feature-requests. */}
        <Route
          path="/feature-requests"
          element={isAdmin ? <FeatureRequestsPage /> : <NoAccess what="Feature requests" />}
        />
        {/* Processes, moved in from the standalone EOS app. Admin-only rather
            than card-gated: the list of what the business does, who owns each
            one and the maturity level it belongs to is the shape of the
            company, administered next to the directory it names people from. */}
        <Route path="/processes" element={isAdmin ? <ProcessesPage /> : <NoAccess what="Processes" />} />
        {/* The library those processes are measured out of. Same gate: it is
            the vocabulary the rest of the app measures itself in. */}
        <Route
          path="/scorecard-items"
          element={isAdmin ? <ScorecardItemsPage /> : <NoAccess what="Scorecard Items" />}
        />
        {/* Integrations hold API secrets. Same gate as requireAdmin on
            /api/integrations. */}
        <Route
          path="/integrations"
          element={isAdmin ? <IntegrationsPage /> : <NoAccess what="Integrations" />}
        />
        {/* Under Settings, admin-only like the rest of it. */}
        <Route
          path="/autotask-map"
          element={isAdmin ? <AutotaskMapPage /> : <NoAccess what="Autotask Data Map" />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
